import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge cannot classify the design system's custom `text-*`
 * font-size utilities (text-body, text-h1, …) and lumps them into the same
 * conflict group as `text-<color>` classes — so `cn('text-background',
 * 'text-body')` silently DROPPED the color. That is how the footer
 * newsletter's SUBSCRIBE button lost `text-background` and rendered
 * light-on-green at 1.81:1 (uiux-6fmjeb marker 2). Declaring the custom
 * font-size scale lets both class kinds coexist.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        {
          text: ['display', 'h1', 'h2', 'h3', 'h4', 'body-lg', 'body', 'small'],
        },
      ],
    },
  },
});

/**
 * Merges Tailwind CSS classes with proper precedence handling.
 * Combines clsx for conditional classes with tailwind-merge for deduplication.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Formats a date string into a human-readable format.
 * @param date - ISO date string or Date object
 * @param locale - BCP 47 language tag, defaults to 'en-CA'
 */
export function formatDate(
  date: string | Date,
  locale: string = 'en-CA',
): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));
}

/**
 * Lowercase, hyphen-separated, alphanumeric-only slug.
 * Stable across runs — used for anchor IDs and JSON-LD `@id` fragments so
 * URLs like `/shopify-pricing#shopify-website-development` deep-link to a
 * specific pricing card.
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Article URLs are intentionally FLAT — served and canonicalised at the root
 * (`/{slug}`), with no `/blog/` prefix (per the blog taxonomy spec). The
 * category and tag archives under `/blog/...` only ever link to articles; they
 * never host them in their own path.
 */
export function blogArticlePath(slug: string): string {
  return `/${slug}`;
}

/**
 * Category archive path: `/blog/{slug}`. Flat under `/blog` (no `/category`
 * segment) — the short URL is the canonical one, and the legacy
 * `/blog/category/{slug}` form 301-redirects here (see next.config.ts).
 */
export function blogCategoryPath(categorySlug: string): string {
  return `/blog/${categorySlug}`;
}

/** Tag archive path (service + format tags share this namespace): `/blog/tag/{slug}`. */
export function blogTagPath(tagSlug: string): string {
  return `/blog/tag/${tagSlug}`;
}

/**
 * Capitalizes only the first letter of a string, leaving the rest untouched
 * (e.g. "store from scratch" → "Store from scratch"). Used for display labels
 * where tag titles are stored lowercase.
 */
export function capitalizeFirst(input: string): string {
  return input ? input.charAt(0).toUpperCase() + input.slice(1) : input;
}

/**
 * Fallback category slug used by the migration when a post has no category.
 * Articles don't need a category for their (flat) URL, so this only affects
 * archive membership + the breadcrumb until an editor assigns the real one.
 */
export const BLOG_FALLBACK_CATEGORY_SLUG = 'development';
